import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiPlus, FiSearch, FiChevronLeft, FiChevronRight, FiDownload, FiUserMinus, FiUpload, FiX, FiCheckCircle, FiAlertCircle, FiAlertTriangle } from 'react-icons/fi';
import { userService, departmentService } from '../../services/api';
import { exportToExcel } from '../../utils/exportExcel';
import * as XLSX from 'xlsx';
import './Admin.css';

const EMPLOYMENT_TYPES = ['Full-time', 'Part-time', 'Contract', 'Temporary'];
const STATUSES = ['Active', 'Inactive', 'On Leave'];

const EmployeeList = () => {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('all');
  const [filterEmploymentType, setFilterEmploymentType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const itemsPerPage = 10;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [empRes, deptRes] = await Promise.all([
        userService.getAll({ limit: 1000 }),
        departmentService.getAll({ limit: 1000 }),
      ]);
      setEmployees(empRes.data.users || []);
      setDepartments(deptRes.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load employees');
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredEmployees = employees.filter(emp => {
    const matchesSearch =
      `${emp.firstName} ${emp.lastName}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
      emp.email.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesDepartment = filterDepartment === 'all' || emp.departmentId === filterDepartment;
    const matchesType = filterEmploymentType === 'all' || emp.employmentType === filterEmploymentType;
    const matchesStatus = filterStatus === 'all' || (emp.isActive ? 'Active' : 'Inactive') === filterStatus;

    return matchesSearch && matchesDepartment && matchesType && matchesStatus;
  });

  const totalPages = Math.ceil(filteredEmployees.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedEmployees = filteredEmployees.slice(startIndex, startIndex + itemsPerPage);

  const getDepartmentName = (deptId) => {
    return departments.find(d => d.id === deptId)?.name || 'Unassigned';
  };

  const handleRowClick = (employeeId) => {
    navigate(`/admin/employees/${employeeId}`);
  };

  const handleAddEmployee = () => {
    navigate('/admin/employees/new');
  };

  const handleDeactivate = async (e, empId, empName) => {
    e.stopPropagation();
    if (!window.confirm(`Are you sure you want to move "${empName}" to ex-employee? They will no longer be able to log in.`)) {
      return;
    }
    try {
      await userService.update(empId, { isActive: false, endDate: new Date().toISOString().split('T')[0] });
      setError(null);
      fetchData();
    } catch (err) {
      setError('Failed to deactivate employee');
      console.error(err);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handleDownloadTemplate = () => {
    const templateData = [
      {
        'First Name': 'John', 'Last Name': 'Doe', 'Email': 'john.doe@company.com',
        'Phone': '+1234567890', 'Job Title': 'Software Engineer', 'Department': 'Engineering',
        'Employment Type': 'full_time', 'Join Date': '2024-01-15', 'End Date': '',
        'Date of Birth': '1990-05-20', 'Address': '123 Main St, London',
        'NI Number': 'AB123456C', 'Emergency Contact Name': 'Jane Doe',
        'Emergency Contact Phone': '+0987654321', 'Manager Email': 'manager@company.com',
        'Status': 'Active'
      },
      {
        'First Name': 'Ex', 'Last Name': 'Employee', 'Email': 'ex.employee@company.com',
        'Phone': '+1111111111', 'Job Title': 'Designer', 'Department': 'Marketing',
        'Employment Type': 'full_time', 'Join Date': '2020-03-01', 'End Date': '2024-06-30',
        'Date of Birth': '1985-08-10', 'Address': '456 Old Road, London',
        'NI Number': 'CD234567E', 'Emergency Contact Name': 'Someone',
        'Emergency Contact Phone': '+2222222222', 'Manager Email': '',
        'Status': 'Inactive'
      }
    ];
    const ws = XLSX.utils.json_to_sheet(templateData);
    ws['!cols'] = [
      { wch: 14 }, { wch: 14 }, { wch: 28 }, { wch: 16 }, { wch: 22 },
      { wch: 18 }, { wch: 16 }, { wch: 14 }, { wch: 14 }, { wch: 14 },
      { wch: 30 }, { wch: 14 }, { wch: 22 }, { wch: 22 }, { wch: 28 }, { wch: 10 }
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Employees');
    XLSX.writeFile(wb, 'Employee_Upload_Template.xlsx');
  };

  const handleBulkUpload = async () => {
    if (!uploadFile) return;
    setUploading(true);
    setUploadResult(null);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      const res = await userService.bulkUpload(formData);
      setUploadResult(res.data);
      if (res.data.created > 0) {
        fetchData();
      }
    } catch (err) {
      setUploadResult({
        message: err.response?.data?.error || 'Upload failed',
        created: 0, skipped: 0, errorCount: 1,
        details: { created: [], skipped: [], errors: [{ row: '-', reason: err.response?.data?.error || 'Upload failed' }] }
      });
    } finally {
      setUploading(false);
    }
  };

  const closeUploadModal = () => {
    setShowUploadModal(false);
    setUploadFile(null);
    setUploadResult(null);
  };

  if (loading) {
    return (
      <div className="admin-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading employees...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <div className="admin-header">
        <div>
          <h1>Employee Management</h1>
          <p className="header-subtitle">Total Employees: {filteredEmployees.length}</p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button className="btn btn-secondary" onClick={() => {
            const data = filteredEmployees.map(emp => ({
              Name: `${emp.firstName} ${emp.lastName}`,
              Email: emp.email,
              Department: getDepartmentName(emp.departmentId),
              'Job Title': emp.jobTitle || '',
              'Employment Type': emp.employmentType || '',
              'Join Date': emp.joinDate ? formatDate(emp.joinDate) : '',
              Phone: emp.phone || '',
              Status: emp.isActive !== false ? 'Active' : 'Inactive',
            }));
            exportToExcel(data, 'Employees');
          }}>
            <FiDownload /> Export Excel
          </button>
          <button className="btn btn-primary" onClick={() => setShowUploadModal(true)}>
            <FiUpload /> Upload Excel
          </button>
          <button className="btn btn-primary" onClick={handleAddEmployee}>
            <FiPlus /> Add Employee
          </button>
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}

      <UploadModal
        show={showUploadModal}
        onClose={closeUploadModal}
        onDownloadTemplate={handleDownloadTemplate}
        uploadFile={uploadFile}
        setUploadFile={setUploadFile}
        onUpload={handleBulkUpload}
        uploading={uploading}
        uploadResult={uploadResult}
      />

      <div className="filters-section">
        <div className="search-box">
          <FiSearch />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>

        <select
          className="filter-select"
          value={filterDepartment}
          onChange={(e) => {
            setFilterDepartment(e.target.value);
            setCurrentPage(1);
          }}
        >
          <option value="all">All Departments</option>
          {departments.map(dept => (
            <option key={dept.id} value={dept.id}>
              {dept.name}
            </option>
          ))}
        </select>

        <select
          className="filter-select"
          value={filterEmploymentType}
          onChange={(e) => {
            setFilterEmploymentType(e.target.value);
            setCurrentPage(1);
          }}
        >
          <option value="all">All Employment Types</option>
          {EMPLOYMENT_TYPES.map(type => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </select>

        <select
          className="filter-select"
          value={filterStatus}
          onChange={(e) => {
            setFilterStatus(e.target.value);
            setCurrentPage(1);
          }}
        >
          <option value="all">All Statuses</option>
          {STATUSES.map(status => (
            <option key={status} value={status}>
              {status}
            </option>
          ))}
        </select>
      </div>

      {paginatedEmployees.length === 0 ? (
        <div className="no-results">
          <p>No employees found matching your criteria.</p>
        </div>
      ) : (
        <>
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Department</th>
                  <th>Job Title</th>
                  <th>Employment Type</th>
                  <th>Join Date</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {paginatedEmployees.map(emp => (
                  <tr key={emp.id} onClick={() => handleRowClick(emp.id)} className="clickable-row">
                    <td className="name-cell">
                      <div className="employee-avatar">{emp.firstName?.[0]}{emp.lastName?.[0]}</div>
                      {emp.firstName} {emp.lastName}
                    </td>
                    <td>{emp.email}</td>
                    <td>{getDepartmentName(emp.departmentId)}</td>
                    <td>{emp.jobTitle || '-'}</td>
                    <td>{emp.employmentType || '-'}</td>
                    <td>{emp.joinDate ? formatDate(emp.joinDate) : '-'}</td>
                    <td>
                      <span className={`status-badge ${emp.isActive !== false ? 'active' : 'inactive'}`}>
                        {emp.isActive !== false ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      {emp.isActive !== false && (
                        <button
                          className="btn btn-sm btn-deactivate"
                          onClick={(e) => handleDeactivate(e, emp.id, `${emp.firstName} ${emp.lastName}`)}
                          title="Move to Ex-Employee"
                        >
                          <FiUserMinus size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="pagination-btn"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                <FiChevronLeft /> Previous
              </button>

              <div className="pagination-info">
                Page {currentPage} of {totalPages} ({filteredEmployees.length} total)
              </div>

              <button
                className="pagination-btn"
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
              >
                Next <FiChevronRight />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// Upload Modal Component
const UploadModal = ({ show, onClose, onDownloadTemplate, uploadFile, setUploadFile, onUpload, uploading, uploadResult }) => {
  if (!show) return null;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content bulk-upload-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Bulk Upload Employees</h2>
          <button className="modal-close-btn" onClick={onClose}><FiX size={20} /></button>
        </div>

        <div className="modal-body">
          <div className="upload-step">
            <h3>Step 1: Download Template</h3>
            <p>Download the Excel template, fill in employee details, then upload it back.</p>
            <button className="btn btn-secondary" onClick={onDownloadTemplate}>
              <FiDownload /> Download Template
            </button>
          </div>

          <div className="upload-step">
            <h3>Step 2: Upload File</h3>
            <p>Upload your filled Excel (.xlsx) or CSV file. Default password for new employees: <strong>Welcome@123</strong></p>
            <p style={{ fontSize: '0.85rem', color: '#6b7280' }}>
              Set Status column to "Inactive" for ex-employees. Leave blank or "Active" for current employees.
            </p>
            <div className="file-input-wrapper">
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={(e) => setUploadFile(e.target.files[0] || null)}
                id="bulk-upload-input"
              />
              {uploadFile && <span className="file-name">{uploadFile.name}</span>}
            </div>
            <button
              className="btn btn-primary"
              onClick={onUpload}
              disabled={!uploadFile || uploading}
              style={{ marginTop: '0.75rem' }}
            >
              {uploading ? 'Uploading...' : <><FiUpload /> Upload & Process</>}
            </button>
          </div>

          {uploadResult && (
            <div className="upload-results">
              <h3>Results</h3>
              <div className="upload-summary">
                {uploadResult.created > 0 && (
                  <div className="result-badge result-success">
                    <FiCheckCircle /> {uploadResult.created} Created
                  </div>
                )}
                {uploadResult.skipped > 0 && (
                  <div className="result-badge result-warning">
                    <FiAlertTriangle /> {uploadResult.skipped} Skipped
                  </div>
                )}
                {uploadResult.errorCount > 0 && (
                  <div className="result-badge result-error">
                    <FiAlertCircle /> {uploadResult.errorCount} Errors
                  </div>
                )}
              </div>

              {uploadResult.details?.errors?.length > 0 && (
                <div className="upload-detail-list">
                  <h4>Errors:</h4>
                  {uploadResult.details.errors.map((e, i) => (
                    <div key={i} className="detail-item error-item">
                      Row {e.row}: {e.reason} {e.email && `(${e.email})`}
                    </div>
                  ))}
                </div>
              )}

              {uploadResult.details?.skipped?.length > 0 && (
                <div className="upload-detail-list">
                  <h4>Skipped (already exist):</h4>
                  {uploadResult.details.skipped.map((s, i) => (
                    <div key={i} className="detail-item warning-item">
                      Row {s.row}: {s.email}
                    </div>
                  ))}
                </div>
              )}

              {uploadResult.details?.created?.length > 0 && (
                <div className="upload-detail-list">
                  <h4>Created:</h4>
                  {uploadResult.details.created.map((c, i) => (
                    <div key={i} className="detail-item success-item">
                      Row {c.row}: {c.name} ({c.email})
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmployeeList;
